import { memo, useState, useCallback, useEffect, useRef } from 'react';
import { Handle, Position } from 'reactflow';
import { Volume2, X, Loader2, CheckCircle, AlertCircle, Play, Download, Clock, Monitor, Maximize2, Upload, Image, Film, Link2, Edit3, Type, Eye } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, inputNeed, textNeed } from '../../utils/nodeRequirements';

const MODES = [
  { id: 'image-to-video', label: 'Imagen', icon: Image, description: 'Anima una imagen' },
  { id: 'video-edit', label: 'Editar', icon: Edit3, description: 'Modifica un video (max 8.7s)' },
  { id: 'text-to-video', label: 'Texto', icon: Type, description: 'Genera video desde texto' },
];

// Preset prompts with Spanish names
const PRESET_PROMPTS = [
  {
    name: "Abrazo tierno",
    prompt: "Animate the people in this image while preserving their exact faces, identity, clothing, and environment. They naturally come to life and embrace each other warmly with a tender hug. Add subtle breathing, small head movements and natural body motion. Preserve the background and animate it slightly. Camera mostly static with slight cinematic drift. Soft natural ambient sound matching the environment, gentle emotional music. Cinematic realism.",
    icon: "🤗"
  },
  {
    name: "Se van de la mano",
    prompt: "Animate the original image with strict identity and scene preservation. Keep the exact same people, preserving facial identity, facial structure, skin tone, hairstyle, body shape, age, clothing, accessories, and character design with no alterations. The people slowly turn around in a natural and coordinated way, gently hold hands, and walk away together, gradually moving farther from the camera. Motion must be smooth, realistic, emotionally soft, and physically coherent, with subtle body movement and natural walking rhythm. Static camera, no zoom, no pan. Realistic, fluid, cinematic motion with ambient sound.",
    icon: "👫"
  },
  {
    name: "Saludo",
    prompt: "Animate the people in this image while preserving their exact faces, identity, clothing, and environment. They naturally come to life and gently wave at the camera in a friendly greeting. Add subtle breathing, small head movements and natural body motion. Preserve the background and animate it slightly. Camera mostly static with slight cinematic drift. Soft natural ambient sound matching the environment, no music. Cinematic realism.",
    icon: "👋"
  },
];

// Video edit preset prompts
const VIDEO_EDIT_PRESETS = [
  { name: "Cambiar fondo", prompt: "Replace the background with a beautiful sunset beach scene", icon: "🌅" },
  { name: "Añadir efecto", prompt: "Add magical sparkles and glowing particles around the subject", icon: "✨" },
  { name: "Estilo anime", prompt: "Transform this into anime style while keeping the motion", icon: "🎨" },
];

// Text-to-video preset prompts
const TEXT_TO_VIDEO_PRESETS = [
  { name: "Pingüino", prompt: "A penguin walks away from the camera, towards a large snowy mountaintop in the distance. Cinematic, dramatic lighting, natural ambient sound of wind and snow.", icon: "🐧" },
  { name: "Atardecer", prompt: "A beautiful golden sunset over the ocean, gentle waves lapping at the shore, seagulls flying in the distance. Peaceful ambient sound with soft music.", icon: "🌅" },
  { name: "Ciudad", prompt: "A bustling city street at night with neon lights, people walking, cars passing by. Urban atmosphere with city sounds and background music.", icon: "🌃" },
];

// Aspect ratio options
const ASPECT_RATIOS = [
  { value: "16:9", label: "16:9", icon: "▬" },
  { value: "9:16", label: "9:16", icon: "▮" },
  { value: "1:1", label: "1:1", icon: "▢" },
  { value: "auto", label: "Auto", icon: "○" },
];

// Helper to fetch file from URL
const fetchFileFromUrl = async (url, filename) => blobToFile(url, filename);

const VideoWithSoundNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [duration, setDuration] = useState(data.duration || 5);
  const [resolution, setResolution] = useState(data.resolution || '720p');
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || 'auto');
  const [mode, setMode] = useState(data.mode || 'image-to-video');
  
  // Local upload states
  const [imageLocal, setImageLocal] = useState(null);
  const [videoLocal, setVideoLocal] = useState(null);
  
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);
  
  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';
  const { openVideoPreview } = useMediaPreview();

  // El prompt tiene que persistirse en node.data: si vive sólo en el estado
  // de React, el servidor no lo recibe (validate_workflow lo marca como
  // faltante) y se pierde al recargar el proyecto.
  useEffect(() => {
    updateNodeData(id, { prompt });
  }, [id, prompt, updateNodeData]);

  // Check connections for image input (handle id: input)
  const imageConnection = edges.find(e => e.target === id && e.targetHandle === 'input');
  const imageSourceNode = imageConnection ? nodes.find(n => n.id === imageConnection.source) : null;
  const imageFromConnection = imageSourceNode?.data?.hasOutput ? {
    file: imageSourceNode.data.file,
    preview: imageSourceNode.data.outputUrl || imageSourceNode.data.preview,
    name: imageSourceNode.data.fileName || 'connected_image.jpg',
    fromConnection: true
  } : null;

  // Check connections for video input (handle id: videoInput)
  const videoConnection = edges.find(e => e.target === id && e.targetHandle === 'videoInput');
  const videoSourceNode = videoConnection ? nodes.find(n => n.id === videoConnection.source) : null;
  const videoFromConnection = videoSourceNode?.data?.hasOutput ? {
    file: videoSourceNode.data.file,
    preview: videoSourceNode.data.outputUrl || videoSourceNode.data.preview,
    name: videoSourceNode.data.fileName || 'connected_video.mp4',
    fromConnection: true
  } : null;

  // Determine final sources
  const imageSource = imageFromConnection || imageLocal;
  const videoSource = videoFromConnection || videoLocal;

  const handleSourcePickImage = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        const reader = new FileReader();
        reader.onload = (e) => setImageLocal({ file: result.file, preview: e.target.result, name: result.file.name });
        reader.readAsDataURL(result.file);
      } else if (result.url) {
        setImageLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, openSourcePicker]);

  const handleSourcePickVideo = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        setVideoLocal({ file: result.file, preview: URL.createObjectURL(result.file), name: result.file.name });
      } else if (result.url) {
        setVideoLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
      }
    });
    openSourcePicker(id, ['video'], 'video/mp4,video/mov,video/webm');
  }, [id, openSourcePicker]);

  // Update node data when local files change
  useEffect(() => {
    if (imageLocal || videoLocal) {
      updateNodeData(id, {
        hasInput: true,
        mode
      });
    }
  }, [imageLocal, videoLocal, id, updateNodeData, mode]);

  // Chronometer effect
  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => {
        setChronometer((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePresetClick = (preset) => {
    setPrompt(preset.prompt);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      setImageLocal({
        file,
        preview,
        name: file.name
      });
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      setVideoLocal({
        file,
        preview,
        name: file.name
      });
    }
  };

  const clearImageLocal = () => {
    if (imageLocal?.preview) URL.revokeObjectURL(imageLocal.preview);
    setImageLocal(null);
  };

  const clearVideoLocal = () => {
    if (videoLocal?.preview) URL.revokeObjectURL(videoLocal.preview);
    setVideoLocal(null);
  };

  const handleProcess = useCallback(async () => {
    if (!prompt.trim()) return;

    let imageFile = imageSource?.file;
    let videoFile = videoSource?.file;
    const directParams = { prompt, duration, resolution, aspect_ratio: aspectRatio };
    const files = [];

    if (!imageFile && imageSource?.preview) {
      if (imageSource.preview.startsWith('http')) {
        directParams.image_url = imageSource.preview;
      } else {
        imageFile = await fetchFileFromUrl(imageSource.preview, imageSource.name);
      }
    }
    if (!videoFile && videoSource?.preview) {
      if (videoSource.preview.startsWith('http')) {
        directParams.video_url = videoSource.preview;
      } else {
        videoFile = await fetchFileFromUrl(videoSource.preview, videoSource.name);
      }
    }

    resetExec();

    if (mode === 'image-to-video') {
      if (imageFile) files.push({ key: 'image_url', file: imageFile });
    } else if (mode === 'video-edit') {
      if (videoFile) files.push({ key: 'video_url', file: videoFile });
    }

    const result = await processNode({
      resultType: 'video_with_sound',
      files,
      params: directParams,
    });

    if (result?.outputUrl) {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'video',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'video',
        tool: 'videoSound',
        parent: mode === 'image-to-video' ? imageSource?.name : videoSource?.name,
        filename: `video_sound_${Date.now()}.mp4`,
        url: result.outputUrl,
        prompt,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, mode, imageSource, videoSource, updateNodeData, addAsset, prompt, duration, resolution, aspectRatio, processNode, resetExec]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const showCostWarning = duration > 5;
  const currentPresets = mode === 'image-to-video' 
    ? PRESET_PROMPTS 
    : mode === 'video-edit' 
      ? VIDEO_EDIT_PRESETS 
      : TEXT_TO_VIDEO_PRESETS;

  // Check if we have valid input based on mode (text-to-video doesn't need input)
  const hasValidInput = mode === 'image-to-video' 
    ? !!imageSource 
    : mode === 'video-edit' 
      ? !!videoSource 
      : true; // text-to-video always has valid input (just needs prompt)

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([mode === 'image-to-video' ? inputNeed('image', !!imageSource, { connected: !!imageSourceNode }) : null, mode === 'video-edit' ? inputNeed('video', !!videoSource, { connected: !!videoSourceNode }) : null, textNeed(prompt)]);

  return (
    <div className={`node-container videoSound-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="video-sound-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="videowithsound-reconfigure-notice"
        />
      )}
      {/* Image Input Handle (for image-to-video mode) */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="node-handle input-handle"
        style={{ top: '30%' }}
        data-testid="video-sound-image-input"
      />
      
      {/* Video Input Handle (for video-edit mode) */}
      <Handle
        type="target"
        position={Position.Left}
        id="videoInput"
        className="node-handle input-handle video-input-handle"
        style={{ top: '55%' }}
        data-testid="video-sound-video-input"
      />
      
      {/* Header */}
      <div className="node-header videoSound-header">
        <div className="node-header-left">
          <Volume2 size={16} />
          <span>Video con Sonido</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="video-sound-delete">
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content videoSound-content">
        {/* Mode Selector */}
        <div className="videoSound-mode-selector" data-testid="video-sound-mode-selector">
          {MODES.map((m) => (
            <button
              key={m.id}
              className={`videoSound-mode-btn ${mode === m.id ? 'active' : ''}`}
              onClick={() => setMode(m.id)}
              title={m.description}
              data-testid={`mode-${m.id}`}
            >
              <m.icon size={14} />
              <span>{m.label}</span>
            </button>
          ))}
        </div>

        {/* Input Section based on mode */}
        <div className="videoSound-inputs">
          {mode === 'image-to-video' ? (
            <div className="videoSound-input-section">
              <label>
                <Image size={12} />
                Imagen de entrada
              </label>
              {imageSource ? (
                <div className="videoSound-input-preview">
                  <img src={imageSource.preview} alt="Input" />
                  <div className="input-source-badge">
                    {imageSource.fromConnection ? <Link2 size={10} /> : <Upload size={10} />}
                    <span>{imageSource.fromConnection ? 'Conectado' : imageSource.name}</span>
                  </div>
                  {!imageSource.fromConnection && (
                    <button className="input-clear-btn" onClick={clearImageLocal}>
                      <X size={12} />
                    </button>
                  )}
                </div>
              ) : (
                <div 
                  className="videoSound-upload-zone"
                >
                  <Upload size={16} />
                  <span>Cargar imagen o conectar</span>
                  <button type="button" className="node-load-btn" onClick={handleSourcePickImage} data-testid="load-btn-vws-image">
                    <Upload size={12} /> Cargar
                  </button>
                </div>
              )}
            </div>
          ) : mode === 'video-edit' ? (
            <div className="videoSound-input-section">
              <label>
                <Film size={12} />
                Video a editar (max 8.7s)
              </label>
              {videoSource ? (
                <div className="videoSound-input-preview video-preview">
                  <video src={videoSource.preview} muted />
                  <div className="input-source-badge">
                    {videoSource.fromConnection ? <Link2 size={10} /> : <Upload size={10} />}
                    <span>{videoSource.fromConnection ? 'Conectado' : videoSource.name}</span>
                  </div>
                  {!videoSource.fromConnection && (
                    <button className="input-clear-btn" onClick={clearVideoLocal}>
                      <X size={12} />
                    </button>
                  )}
                </div>
              ) : (
                <div 
                  className="videoSound-upload-zone"
                >
                  <Upload size={16} />
                  <span>Cargar video o conectar</span>
                  <button type="button" className="node-load-btn" onClick={handleSourcePickVideo} data-testid="load-btn-vws-video">
                    <Upload size={12} /> Cargar
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Text-to-video mode - no file input needed
            <div className="videoSound-text-mode-info">
              <Type size={14} />
              <span>Solo necesitas un prompt para generar el video</span>
            </div>
          )}
        </div>

        {/* Processing State */}
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">
              {formatTime(chronometer)}
            </div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>{mode === 'image-to-video' ? 'Generando video...' : 'Editando video...'}</span>
            </div>
            <p className="node-processing-hint">
              {mode === 'image-to-video' ? `Video de ${duration}s` : 'Editando'} ({resolution})
            </p>
          </div>
        ) : !showForm ? (
          <div className="node-result">
            <video 
              src={data.outputUrl} 
              controls 
              className="node-result-video clickable"
              onClick={() => openVideoPreview(data.outputUrl, 'Video con Sonido')}
            />
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="videowithsound-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="videowithsound-reconfigure-btn" />
              <button 
                className="node-mini-preview-btn"
                onClick={() => openVideoPreview(data.outputUrl, 'Video con Sonido')}
              >
                <Eye size={12} />
                Ver
              </button>
              <button 
                className="node-mini-download-btn"
                onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `video_sound_${Date.now()}.mp4`); }}
              >
                <Download size={12} />
                Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="video" label="videowithsound" testIdPrefix="videowithsound-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Video creado</span>
            </div>
          </div>
        ) : (
          <>
            {/* Duration & Resolution Settings */}
            <div className="videoSound-settings">
              {(mode === 'image-to-video' || mode === 'text-to-video') && (
                <div className="videoSound-setting">
                  <label>
                    <Clock size={12} />
                    Duración
                  </label>
                  <div className="videoSound-duration-row">
                    <input
                      type="range"
                      min="1"
                      max="15"
                      value={duration}
                      onChange={(e) => setDuration(parseInt(e.target.value))}
                      className="videoSound-slider"
                      data-testid="duration-slider"
                    />
                    <span className="videoSound-duration-value">{duration}s</span>
                  </div>
                </div>
              )}
              
              <div className="videoSound-setting">
                <label>
                  <Monitor size={12} />
                  Resolución
                </label>
                <div className="videoSound-options">
                  {['720p', '1080p'].map((r) => (
                    <button
                      key={r}
                      className={`videoSound-option ${resolution === r ? 'active' : ''}`}
                      onClick={() => setResolution(r)}
                      data-testid={`resolution-${r}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              
              {(mode === 'image-to-video' || mode === 'text-to-video') && (
                <div className="videoSound-setting">
                  <label>
                    <Maximize2 size={12} />
                    Aspecto
                  </label>
                  <div className="videoSound-options aspect-options">
                    {ASPECT_RATIOS.map((ar) => (
                      <button
                        key={ar.value}
                        className={`videoSound-option aspect-option ${aspectRatio === ar.value ? 'active' : ''}`}
                        onClick={() => setAspectRatio(ar.value)}
                        title={ar.label}
                        data-testid={`aspect-${ar.value}`}
                      >
                        <span className="aspect-icon">{ar.icon}</span>
                        <span className="aspect-label">{ar.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Cost Warning */}
            {showCostWarning && (mode === 'image-to-video' || mode === 'text-to-video') && (
              <div className="videoSound-warning">
                <AlertCircle size={12} />
                <span>Duración de {duration}s tiene costo más elevado</span>
              </div>
            )}

            {/* Preset Prompts */}
            <div className="videoSound-presets">
              <label>Prompts predefinidos:</label>
              <div className="videoSound-preset-btns">
                {currentPresets.map((preset) => (
                  <button
                    key={preset.name}
                    className={`videoSound-preset-btn ${prompt === preset.prompt ? 'active' : ''}`}
                    onClick={() => handlePresetClick(preset)}
                    title={preset.name}
                    data-testid={`preset-${preset.name}`}
                  >
                    <span className="preset-icon">{preset.icon}</span>
                    <span className="preset-name">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Prompt */}
            <div className="videoSound-prompt-section">
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="videoWithSound"
                placeholder={mode === 'image-to-video'
                  ? "Describe el movimiento, la acción y el ambiente del video..."
                  : mode === 'video-edit'
                    ? "Describe qué cambios quieres hacer al video..."
                    : "Describe la escena que quieres crear con detalle..."}
                className="videoSound-prompt-input"
                rows={3}
                testId="prompt-textarea"
              />
            </div>

            {/* Process Button */}
            <button
              className="node-process-btn videoSound-process-btn"
              onClick={handleProcess}
              disabled={!hasValidInput || !prompt.trim()}
              title={requirementsTitle(runMissing)}
              data-testid="generate-btn"
            >
              <Play size={14} />
              {mode === 'image-to-video' ? 'Generar Video' : mode === 'video-edit' ? 'Editar Video' : 'Crear Video'}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        {/* Error Display */}
        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>
      
      {/* Output Handle - Always connectable */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        data-testid="video-sound-output"
      />
    </div>
  );
});

VideoWithSoundNode.displayName = 'VideoWithSoundNode';

export default VideoWithSoundNode;
